import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Blue2Page } from "../Page";
import { CLUB, Draw, EASE, HOME, PAGES } from "../bits";
import "./NotFound.css";

/* blue-2's 404: a page that came unstitched, with a thread trailing off a
   spool and links back to the main pages. */

const PLACES = [
  { to: HOME, label: "Home", note: "start over" },
  { to: PAGES.about, label: "About me", note: "hi, it’s me!" },
  { to: PAGES.videos, label: "Videos", note: "new every week" },
  { to: CLUB, label: "The club", note: "sew with me live" },
  { to: PAGES.favorites, label: "Favorites", note: "stuff I love" },
  { to: PAGES.retreats, label: "Retreats", note: "sew away" },
];

/* A thread from the spool, looping once, ending in a dangling loose end. */
const THREAD = "M70 92 C 150 110, 190 40, 260 70 C 330 100, 300 170, 240 160 C 180 150, 210 90, 290 100 C 380 112, 420 200, 470 230 C 500 248, 520 262, 512 300";

function Header() {
  return <div className="bnf-head">
    <div className="bnf-copy">
      <motion.p className="bnf-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Error 404 · page not found</motion.p>
      <h1 className="bnf-title">
        <motion.span className="bnf-hand" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.45, duration: 0.5, ease: EASE }}>well, that’s a</motion.span>
        <span className="bnf-word" aria-label="loose thread">
          {["loose", "thread"].map((w, wi) => <span key={w} className="bnf-w" aria-hidden="true">
            {w.split("").map((c, i) => <motion.span key={i} initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + (wi * 6 + i) * 0.035, duration: 0.6, ease: EASE }}>{c}</motion.span>)}
          </span>)}
        </span>
      </h1>
      <motion.p className="bnf-text" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.5, ease: EASE }}>
        This page came unstitched. It may have moved, or the link has a dropped stitch. Let’s get you back to the good stuff.
      </motion.p>
      <motion.div className="bnf-actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.5, ease: EASE }}>
        <Link to={HOME} className="bnf-btn">Take me home <ArrowRight size={18} aria-hidden="true" /></Link>
      </motion.div>
    </div>
    <div className="bnf-art" aria-hidden="true">
      <svg viewBox="0 0 540 340">
        <g className="bnf-thread"><Draw d={THREAD} delay={0.6} width={4} /></g>
        <g className="bnf-spool" transform="translate(18 34) rotate(-10 52 60)">
          <rect x="6" y="0" width="92" height="16" rx="5" />
          <rect x="18" y="16" width="68" height="88" className="bnf-spool-thread" />
          <path d="M18 34 H86 M18 50 H86 M18 66 H86 M18 82 H86" className="bnf-spool-lines" />
          <rect x="6" y="104" width="92" height="16" rx="5" />
        </g>
        <g className="bnf-needle" transform="rotate(32 512 300)">
          <path d="M507 300 L 512 372 L 517 300 Z" />
          <ellipse cx="512" cy="306" rx="2.4" ry="5" className="bnf-eye" />
        </g>
      </svg>
      <p className="bnf-note">oops, it got away from me!</p>
    </div>
  </div>;
}

export function Blue2NotFound() {
  return <Blue2Page tone="blush" header={<Header />}>
    <section className="bnf-places" aria-labelledby="bnf-places-title">
      <div className="b2-wrap">
        <p id="bnf-places-title" className="b2-note bnf-places-title">pick up a new thread:</p>
        <ul>{PLACES.map((p, i) => <motion.li key={p.to} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, rotate: [-1.5, 1, -0.8, 1.4, -1, 0.8][i] }} transition={{ delay: 0.3 + i * 0.06, duration: 0.5, ease: EASE }}>
          <Link to={p.to}><b>{p.label}</b><span>{p.note}</span><ArrowRight size={18} aria-hidden="true" /></Link>
        </motion.li>)}</ul>
      </div>
    </section>
  </Blue2Page>;
}
